/**
 * Content Image Slider — frontend Swiper initialization.
 *
 * Reads data attributes from the PHP-rendered wrapper and bootstraps
 * Swiper with EffectFade, Pagination, optional Autoplay and A11y.
 *
 * Depends on the global `window.WombatSwiper` (registered as `wombat-swiper`).
 *
 * @package
 */

/**
 * Unwrap an anonymous InnerBlocks wrapper div so that .swiper-slide
 * elements become direct children of .swiper-wrapper.
 *
 * WordPress wraps rendered InnerBlocks content in an extra <div>.
 * Swiper requires slides to be immediate children of the wrapper.
 *
 * @param {HTMLElement} swiperEl The .swiper container element.
 */
const unwrapInnerBlocksWrapper = ( swiperEl ) => {

	const wrapper = swiperEl.querySelector( ".swiper-wrapper" );

	if ( !wrapper ) {

		return;

	}

	// If the first (and only) direct child is NOT a .swiper-slide,
	// it is the anonymous InnerBlocks wrapper — move its children up.
	const firstChild = wrapper.firstElementChild;

	if (
		firstChild &&
		!firstChild.classList.contains( "swiper-slide" ) &&
		firstChild.querySelector( ".swiper-slide" )
	) {

		while ( firstChild.firstChild ) {

			wrapper.insertBefore( firstChild.firstChild, firstChild );

		}

		firstChild.remove();

	}

};

/**
 * Move the shared pagination element into currently active slide content.
 *
 * This keeps dots visually inside the text frame (under content, above decorations)
 * without changing the existing pagination styling.
 *
 * @param {HTMLElement} block  The content-image-slider block root.
 * @param {Object} swiper Swiper instance.
 */
const movePaginationToActiveSlide = ( block, swiper ) => {

	const paginationEl = block.querySelector(
		".b-content-image-slider__pagination",
	);

	if ( !paginationEl || !swiper?.slides?.length ) {

		return;

	}

	const activeSlide = swiper.slides[swiper.activeIndex];

	if ( !activeSlide ) {

		return;

	}

	const contentInnerEl = activeSlide.querySelector(
		".b-content-image-slider__content-inner",
	);

	if ( !contentInnerEl || paginationEl.parentElement === contentInnerEl ) {

		return;

	}

	contentInnerEl.appendChild( paginationEl );

};

/**
 * Initialise Swiper instances for all content-image-slider blocks on the page.
 */
const initSliders = () => {

	const { Swiper, Pagination, EffectFade, Autoplay, A11y } =
		window.WombatSwiper;

	document.querySelectorAll( ".b-content-image-slider" ).forEach( ( block ) => {

		const swiperEl = block.querySelector( ".js-content-image-swiper" );

		if ( !swiperEl ) {

			return;

		}

		// WordPress wraps InnerBlocks in an extra <div>; unwrap it
		// so Swiper can find .swiper-slide as direct children.
		unwrapInnerBlocksWrapper( swiperEl );

		const isAutoplay = block.dataset.autoplay === "true";
		const delay = parseInt( block.dataset.delay, 10 ) || 5000;

		/** @type {Array} Swiper modules to use. */
		const modules = [Pagination, EffectFade, A11y];

		if ( isAutoplay ) {

			modules.push( Autoplay );

		}

		/**
		 * Handle Swiper init event.
		 *
		 * @param {Object} swiper Swiper instance.
		 */
		const handleInit = ( swiper ) => {

			movePaginationToActiveSlide( block, swiper );

		};

		/**
		 * Handle Swiper slide transition start event.
		 *
		 * @param {Object} swiper Swiper instance.
		 */
		const handleSlideChangeTransitionStart = ( swiper ) => {

			movePaginationToActiveSlide( block, swiper );

		};

		/** @type {Object} Swiper configuration. */
		const config = {
			modules,
			effect: "fade",
			fadeEffect: {
				crossFade: true,
			},
			speed: 600,
			pagination: {
				el: block.querySelector( ".b-content-image-slider__pagination" ),
				clickable: true,
				bulletClass: "b-content-image-slider__dot",
				bulletActiveClass: "b-content-image-slider__dot--active",
			},
			a11y: {
				enabled: true,
				prevSlideMessage: "Poprzedni slajd",
				nextSlideMessage: "Następny slajd",
				paginationBulletMessage: "Przejdź do slajdu {{index}}",
			},
			on: {
				init: handleInit,
				slideChangeTransitionStart: handleSlideChangeTransitionStart,
			},
		};

		if ( isAutoplay ) {

			config.autoplay = {
				delay,
				disableOnInteraction: false,
				pauseOnMouseEnter: true,
			};

		}

		// eslint-disable-next-line no-new
		new Swiper( swiperEl, config );

	} );

};

if ( document.readyState === "loading" ) {

	document.addEventListener( "DOMContentLoaded", initSliders );

} else {

	initSliders();

}
